import { FormColumns, FormLabel } from "components/form";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { useEffect } from "react";
import { addDays } from "date-fns";
import AmountWithCurrencyInput from "components/form/amount-currency-billable";
import ButtonWithLoading from "@/components/button-with-loading";
import DashboardMenu from "@/components/menu";
import { DatePicker } from "components/date-picker-2";
import { DropdownMenuItem } from "ui/components/dropdown-menu";
import { DropdownMenuSeparator } from "ui/components/dropdown-menu";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import EmployeeInput from "components/employee/input";
import MilestoneInput from "components/milestone/input";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import React from "react";
import { StatusTabInput } from "components/status/tabs/input";
import { Textarea } from "ui/components/textarea";
import { billableSchema } from "./schema";
import { useHotkeys } from "react-hotkeys-hook";
import { useBillableForm } from "@/modules/work/billables/details/form/useBillableForm";
import HeaderPortal from "components/portal/header-portal";

const BillableForm = ({ billableId, handleClose, jobId, job, type }) => {
  const permissionRules = {
    create: "create:SC,AM",
    update: "update:SC,AM",
  };

  useEffect(() => {
    form.setValue("type", type);
  }, [type]);

  const {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
    handleCancel,
    handleArchive,
  } = useBillableForm({ defaultJobId: jobId, defaultJobType: type });
  const status = form.watch("status");

  useEffect(() => {
    const dueDays = form.watch("due_days");
    const billingDate = form.watch("billing_date");

    if (
      billingDate &&
      dueDays !== undefined &&
      dueDays !== null &&
      !isNaN(Number(dueDays))
    ) {
      const newDueDate = addDays(new Date(billingDate), Number(dueDays));
      const currentDueDate = form.getValues("due_date");
      const isDifferent =
        !currentDueDate ||
        new Date(currentDueDate).getTime() !== newDueDate.getTime();

      if (isDifferent) {
        form.setValue("due_date", newDueDate, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    }
  }, [billableId, form.watch("due_days"), form.watch("billing_date")]);

  const selectedMilestones = form.watch("milestones");
  const isMilestonesDirty = form.formState.dirtyFields.milestones;

  useEffect(() => {
    if (
      type === "hourly" &&
      Array.isArray(selectedMilestones) &&
      (!billableId || isMilestonesDirty)
    ) {
      if (selectedMilestones.length > 0) {
        // Skip calculation if milestones are just IDs (not fully fetched objects yet)
        const hasIncompleteData = selectedMilestones.some(
          (curr) => typeof curr !== "object" || curr === null,
        );
        if (hasIncompleteData) return;
      }

      const totalHours = selectedMilestones.reduce((acc, curr) => {
        if (curr && typeof curr === "object" && curr.billable_hours) {
          return acc + Number(curr.billable_hours);
        }
        return acc;
      }, 0);

      const currentHours = form.getValues(
        "hourly_billing_config.billable_hours",
      );
      if (currentHours !== totalHours) {
        form.setValue("hourly_billing_config.billable_hours", totalHours, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [billableId, selectedMilestones, type, form]);

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={billableId || "+"}
      schema={billableSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating && !isUpdating) {
              if (isNewItem) createItem().then(handleClose);
              else updateItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );

        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? permissionRules.create
                        : permissionRules.update
                    }
                    collection="billables"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Status
                    </FormLabel>
                    <StatusTabInput
                      {...field}
                      remove={["archived", "cancelled"]}
                      collection="billables"
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? permissionRules.create
                        : permissionRules.update
                    }
                    collection="billables"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Name
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {["hourly", "recurring"].includes(type) && (
              <>
                <FormField
                  control={form.control}
                  name="milestones"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="billables"
                        type="form"
                        field={"milestones"}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Milestones
                        </FormLabel>

                        <MilestoneInput
                          {...field}
                          jobId={jobId}
                          multiselect
                          allowRemoval
                          filters={
                            type === "hourly"
                              ? { status: { _eq: "completed" } }
                              : type === "recurring"
                                ? {
                                    status: {
                                      _in: ["in_progress", "completed"],
                                    },
                                  }
                                : {}
                          }
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="attributed_to"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? permissionRules.create
                          : permissionRules.update
                      }
                      collection="billables"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Attributed To
                      </FormLabel>
                      <EmployeeInput
                        {...field}
                        filters={
                          isNewItem
                            ? {
                                _or: [
                                  {
                                    project_access: {
                                      job: {
                                        _eq: jobId,
                                      },
                                      role: {
                                        _in: [
                                          "account_manager",
                                          "sales_coordinator",
                                        ],
                                      },
                                    },
                                  },
                                ],
                              }
                            : {
                                _or: [
                                  {
                                    project_access: {
                                      job: {
                                        billables: {
                                          _eq: billableId,
                                        },
                                      },
                                      role: {
                                        _in: [
                                          "account_manager",
                                          "sales_coordinator",
                                        ],
                                      },
                                    },
                                  },
                                ],
                              }
                        }
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="billables"
                        field={field.name}
                        type="form"
                        isDisabled={type === "hourly" || type === "dedicated"}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Amount
                        </FormLabel>

                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            )
                          }
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="billing_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? permissionRules.create
                          : permissionRules.update
                      }
                      collection="billables"
                      field={field.name}
                      type="form"
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Billing Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            {!isNewItem && (
              <FormColumns>
                <FormField
                  control={form.control}
                  name="due_days"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="billables"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Due Days
                        </FormLabel>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            )
                          }
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="billables"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Due Date
                        </FormLabel>
                        <DatePicker {...field} disabled />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormColumns>
            )}

            <FormColumns>
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? permissionRules.create
                          : permissionRules.update
                      }
                      collection="billables"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Start Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? permissionRules.create
                          : permissionRules.update
                      }
                      collection="billables"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        End Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            {status !== "contracted" &&
              (status === "billed" || status === "collected" ? (
                <FormColumns>
                  <FormField
                    control={form.control}
                    name="invoice_number"
                    render={({ field }) => (
                      <FormItem>
                        <PermissionControlledInput
                          rule={
                            isNewItem
                              ? permissionRules.create
                              : permissionRules.update
                          }
                          collection="billables"
                          type="form"
                          field={field.name}
                        >
                          <FormLabel
                            required={requiredFields.includes(field.name)}
                          >
                            Invoice Number
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </PermissionControlledInput>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="invoice_date"
                    render={({ field }) => (
                      <FormItem>
                        <PermissionControlledInput
                          rule={
                            isNewItem
                              ? permissionRules.create
                              : permissionRules.update
                          }
                          collection="billables"
                          type="form"
                          field={field.name}
                        >
                          <FormLabel
                            required={requiredFields.includes(field.name)}
                          >
                            Invoice Date
                          </FormLabel>
                          <DatePicker {...field} />
                        </PermissionControlledInput>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormColumns>
              ) : null)}

            {status === "billed" || status === "collected" ? (
              <FormField
                control={form.control}
                name="forecast_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={
                        isNewItem
                          ? permissionRules.create
                          : permissionRules.update
                      }
                      collection="billables"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Forecast Date
                      </FormLabel>
                      <DatePicker {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {/* <FormColumns> */}
            <AmountWithCurrencyInput
              form={form}
              amountLabel="Adjusted Amount"
              amountKey="adjusted_amount"
              required={requiredFields}
              disableCurrency={true}
              collection="billables"
              rule={isNewItem ? "create:SC,AM" : "update:SC,AM"}
            />

            {status === "collected" && (
              <FormColumns>
                <FormField
                  control={form.control}
                  name="collected_date"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="billables"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Collected Date
                        </FormLabel>
                        <DatePicker {...field} />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormColumns>
            )}

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={
                      isNewItem
                        ? permissionRules.create
                        : permissionRules.update
                    }
                    collection="billables"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Note
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} className="min-h-32" />
                    </FormControl>
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!["bucket", "recurring"].includes(type) && (
              <h4 className="scroll-m-20 text-lg font-semibold tracking-tight mb-4">
                Billing Config
              </h4>
            )}

            {type === "fixed" && (
              <>
                <FormField
                  control={form.control}
                  name="fixed_billing_config.approved_hours_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="fixed_billing_config"
                        field={"approved_hours_threshold"}
                        type="form"
                      >
                        <FormLabel
                          required={requiredFields.includes(
                            "fixed_billing_config.approved_hours_threshold",
                          )}
                        >
                          Approved Hours Threshold
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            {...field}
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                        </FormControl>
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {type === "hourly" && (
              <>
                <FormField
                  control={form.control}
                  name="hourly_billing_config.billable_hours"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionControlledInput
                        rule={
                          isNewItem
                            ? permissionRules.create
                            : permissionRules.update
                        }
                        collection="hourly_billing_config"
                        type="form"
                        field={"billable_hours"}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Billable Hours
                        </FormLabel>

                        <Input
                          type="number"
                          min="0"
                          {...field}
                          value={field.value}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            )
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {type === "dedicated" && (
              <>
                <FormColumns>
                  <FormField
                    control={form.control}
                    name="dedicated_billing_config.working_days"
                    render={({ field }) => (
                      <FormItem>
                        <PermissionControlledInput
                          rule={
                            isNewItem
                              ? permissionRules.create
                              : permissionRules.update
                          }
                          collection="dedicated_billing_config"
                          type="form"
                          field={"working_days"}
                        >
                          <FormLabel
                            required={requiredFields.includes(field.name)}
                          >
                            Working Days
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              min="0"
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                                )
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          </FormControl>
                        </PermissionControlledInput>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dedicated_billing_config.billable_days"
                    render={({ field }) => (
                      <FormItem>
                        <PermissionControlledInput
                          rule={
                            isNewItem
                              ? permissionRules.create
                              : permissionRules.update
                          }
                          collection="dedicated_billing_config"
                          type="form"
                          field={"billable_days"}
                        >
                          <FormLabel
                            required={requiredFields.includes(field.name)}
                          >
                            Billable Days
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              {...field}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                                )
                              }
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          </FormControl>
                        </PermissionControlledInput>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormColumns>
              </>
            )}

            {/* Action Buttons - Wrapped in permission gates */}
            <HeaderPortal className="flex gap-3">
              {isNewItem ? (
                <PermissionGate
                  rule={permissionRules.create}
                  collection="billables"
                  fallback={
                    <div className="text-sm text-gray-500 italic">
                      You don't have permission to create billable
                    </div>
                  }
                >
                  <ButtonWithLoading
                    variant="default"
                    isLoading={isCreating}
                    onClick={createItem}
                  >
                    Create
                  </ButtonWithLoading>
                </PermissionGate>
              ) : (
                <PermissionGate
                  rule={permissionRules.update}
                  collection="billables"
                  fallback={
                    <div className="text-sm text-gray-500 italic">
                      You don't have permission to update this billable
                    </div>
                  }
                >
                  <ButtonWithLoading
                    variant="default"
                    isLoading={isUpdating}
                    onClick={updateItem}
                    disabled={!isDirty}
                  >
                    Update
                  </ButtonWithLoading>
                  {!isNewItem && (
                    <DashboardMenu>
                      <DropdownMenuItem onClick={handleArchive}>
                        <PermissionControlledInput
                          rule={permissionRules.update}
                          collection="billables"
                        >
                          Archieved
                        </PermissionControlledInput>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleCancel}>
                        <PermissionControlledInput
                          rule={permissionRules.update}
                          collection="billables"
                        >
                          Cancelled
                        </PermissionControlledInput>
                      </DropdownMenuItem>
                    </DashboardMenu>
                  )}
                </PermissionGate>
              )}
            </HeaderPortal>
          </div>
        );
      }}
    </Form>
  );
};

export default BillableForm;
